// Modules
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import React from 'react';
import _ from 'lodash';

import { Page, PageHeader, Tabs, Tab } from 'components/page';
import { RoundLoader } from 'components/widgets/state-indicators/loader';
import { SVGButton } from 'components/widgets/buttons';
import { mergeSocketObject } from 'utils/socket-utils';
import { prepEagerUpdate } from 'utils/eager-update-utils';
import ClosingLastTask from 'components/models/ticket/closing-last-task';
import SVG from 'components/widgets/icons/svg';
import Ticket from 'services/data/ticket';
import isEqualString from 'utils/is-equal-string';
import replaceById from 'utils/replace-by-id';
import * as socket from 'services/socket';
import * as timerActions from 'modules/timers/actions';

import * as actions from './store/actions';
import * as util from './utils/load-ticket';

// Component
class ViewTicket extends React.Component {
  static propTypes = {
    ticketId: PropTypes.string,
    currentUser: PropTypes.object,
    ticket: PropTypes.object,
    children: PropTypes.node,
    fetching: PropTypes.bool,
    fetchingFiles: PropTypes.bool,
    timeReports: PropTypes.array,
    files: PropTypes.array
  };

  static defaultProps = {
    ticket: {}
  };

  constructor(props) {
    super(props);

    this.handleSocketUpdate = this.handleSocketUpdate.bind(this);
    this.saveNewTask = this.saveNewTask.bind(this);
    this.updateTask = this.updateTask.bind(this);
    this.updateTime = this.updateTime.bind(this);
    this.mergeSocketTicket = this.mergeSocketTicket.bind(this);
    this.loadTicket = this.loadTicket.bind(this);
  }

  componentWillMount() {
    this.loadTicket(this.props.ticketId);

    socket.registerSocket('ticket', this.handleSocketUpdate, this.handleSocketUpdate);
  }

  componentWillReceiveProps(nextProps) {
    if (nextProps.ticketId !== this.props.ticketId) {
      actions.clearPageData();
      util.cancelLoadTicket();
      this.loadTicket(nextProps.ticketId);
    }
  }

  componentWillUnmount() {
    socket.deregisterSocket('ticket');
    util.cancelLoadTicket();
    actions.clearPageData();
  }

  mergeSocketTicket(loaded) {
    if (!this.props.ticket) return loaded;

    return mergeSocketObject(loaded, this.props.ticket, ['tasks.checklist'], { keepItemsFilters: [{ status: 'Active' }] });
  }

  loadTicket(id) {
    const ticketId = id || this.props.ticketId;

    util.loadTicket(ticketId, { fetching: true })
      .tap(ticket => Promise.all([
        util.loadTriage(ticket),
        util.loadTasks(ticket),
        util.loadFolder(ticket),
        util.loadTimeReports(ticket),
        util.loadEmails(ticket),
        util.loadPurchaseOrders(ticket)
      ]));
  }

  handleSocketUpdate(payload) {
    // Ignore other tickets
    if (payload && !isEqualString(this.props.ticketId, payload.id)) return;

    util.debouncedLoadTicket(this.props.ticketId, { merge: this.mergeSocketTicket });
  }

  updateTask(task, ticketUpdates) {
    const tasks = replaceById(prepEagerUpdate(task), this.props.ticket.tasks);
    actions.updateSingleTicket(_.assign({ tasks }, ticketUpdates));
  }

  updateTime(timeReport) {
    actions.set({ timeReports: replaceById(timeReport, this.props.timeReports) });
  }

  updateTriage(triageItems) {
    actions.set({ triageItems });
  }

  saveNewTask(description, properties) {
    const task = { description, ...properties };

    return Ticket.saveNewTask(this.props.ticket, task, this.updateTask);
  }

  render() {
    const { ticket, ticketId, fetching, fetchingFiles, files, currentUser, children } = this.props;
    const clonedChildren = React.cloneElement(children, {
      updateTicket: actions.updateSingleTicket,
      saveNewTask: this.saveNewTask,
      updateTask: this.updateTask,
      updateTime: this.updateTime,
      updateTriage: this.updateTriage
    });

    const billable = _.get(ticket, 'agreement.id');

    const headerActions = (
      <div className="header-actions">
        {billable && <SVGButton onClick={() => timerActions.add(ticket)} title="Start Timer" placement="bottom" src="svg/play.svg" className="bg-asbestos text-white rounded-circle p-2 mr-2 ml-1" />}
      </div>
    );

    return (
      <Page className="ticket-page">

        {ticket.showClosingLastTaskModal &&
          <ClosingLastTask
            ticket={ticket}
            updateTask={this.updateTask}
            saveNewTask={this.saveNewTask}
            updateTicket={actions.updateSingleTicket}
            currentUser={currentUser}
          />
        }
        <PageHeader title={(!fetching && ticket.description) || ''} actions={headerActions}>
          <Tabs className="actions-tabs">
            <Tab to={`/tickets/${ticketId}/summary`}>
              <SVG src="svg/ticket.svg" className="ticket" />
              <span>Summary</span>
            </Tab>
            <Tab to={`/tickets/${ticketId}/tasks`}>
              <SVG src="svg/task.svg" className="task-icon" />
              <span>Tasks</span>
            </Tab>
            <Tab to={`/tickets/${ticketId}/time`}>
              <SVG src="svg/time-report.svg" className="time-report" />
              <span>Time</span>
            </Tab>
            <Tab to={`/tickets/${ticketId}/expenses`}>
              <SVG src="svg/expense-bill.svg" className="expense-bill" />
              <span>Purchases</span>
            </Tab>
            <Tab to={`/tickets/${ticketId}/emails`}>
              <SVG src="svg/email.svg" className="email" />
              <span>Emails</span>
            </Tab>
            <Tab to={`/tickets/${ticketId}/files`}>
              <SVG src="svg/google-drive.svg" className="google-drive" />
              <span>Files</span>
              {fetchingFiles && <RoundLoader color="#34495e" />}
              {!fetchingFiles && ticket.folder && <span> ({_.get(files, 'length', 0)})</span>}
            </Tab>
          </Tabs>
        </PageHeader>
        {!fetching && clonedChildren}
      </Page>
    );
  }
}

// Exports
export default connect((state, ownProps) => ({
  fetching: state.tickets.fetching,
  fetchingFiles: state.tickets.fetchingFiles,
  ticket: state.tickets.ticket,
  files: state.tickets.files,
  timeReports: state.tickets.timeReports,
  triageItems: state.tickets.triageItems,
  purchaseOrders: state.tickets.purchaseOrders,
  ticketId: ownProps.routeParams.ticketId,
  currentUser: state.auth.currentUser,
  users: state.pageData.selectUsers
}))(ViewTicket);



// WEBPACK FOOTER //
// ./app/routes/ticket/view-ticket.js